import { useMemo, useState } from 'react';
import {
  DEFAULT_MAX_SMS_PARTS,
  clampSmsToMaxParts,
  getSmsMetrics,
} from 'shared';
import { Link } from 'react-router-dom';
import { When } from 'react-if';

import { Modal } from '../../../common/Atoms/Modal';
import { Button } from '../../../common/Atoms/Button';
import { Heading } from '../../../common/Atoms/Typography/Heading';
import { Paragraph } from '../../../common/Atoms/Typography/Paragraph';
import { useGetSubscriptionMetadata } from '../../hooks/useGetSubscription';
import { useSubscriptions } from '../../hooks/useSubscriptions';
import { useSendSmsOnDemandMutation } from '../../services/api/communicationsApi/communicationsApi';
import { Notification } from '../../../common/Atoms/Notification';

const ON_DEMAND_SMS_MIN_LENGTH = 4;

interface SmsReplyComposerProps {
  organisationId?: string;
  contactId?: number;
  destinationNumber?: string;
  hasSmsDestination: boolean;
  sendSmsDisabledReason?: string;
  onSent?: () => Promise<unknown> | unknown;
}

export function SmsReplyComposer({
  organisationId,
  contactId,
  destinationNumber,
  hasSmsDestination,
  sendSmsDisabledReason = ``,
  onSent,
}: SmsReplyComposerProps) {
  const { subscriptionMetadata, isBillingOwner } = useGetSubscriptionMetadata();
  const { data: subscriptions } = useSubscriptions();
  const [sendSmsOnDemand, { isLoading: isSendingReply, error: sendReplyError }] = useSendSmsOnDemandMutation();
  const [replyBody, setReplyBody] = useState(``);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const smsMetrics = getSmsMetrics(replyBody, DEFAULT_MAX_SMS_PARTS);
  const canReply = !!organisationId && !!contactId && replyBody.trim().length >= ON_DEMAND_SMS_MIN_LENGTH;
  const selectedSubscription = useMemo(() => {
    if (!subscriptionMetadata?.id) {
      return undefined;
    }

    return subscriptions?.find(subscription => subscription.id === subscriptionMetadata.id);
  }, [subscriptionMetadata?.id, subscriptions]);
  const monthlySmsLimit = selectedSubscription?.smsMonthlyLimit || 0;
  const currentSmsUsage = selectedSubscription?.usage?.currentSMSUsage || 0;
  const projectedSmsUsage = currentSmsUsage + smsMetrics.parts;
  const isSmsLimitReached = monthlySmsLimit > 0 && projectedSmsUsage >= monthlySmsLimit;
  const isSmsLimitClose = monthlySmsLimit > 0 && !isSmsLimitReached && projectedSmsUsage >= Math.ceil(monthlySmsLimit * 0.9);

  async function onSendReply() {
    if (!organisationId || !contactId || !replyBody.trim() || !hasSmsDestination || isSmsLimitReached) {
      return;
    }

    await sendSmsOnDemand({
      organisationId,
      contactId,
      body: replyBody.trim(),
      toNumber: destinationNumber,
    }).unwrap();

    setReplyBody(``);
    setConfirmOpen(false);
    await onSent?.();
  }

  return (
    <>
      <div className={ `mt-4 border-t border-gray-200 pt-4` }>
        <label className={ `block text-xs font-medium text-gray-600 mb-2` }>
          { `Reply via SMS` }
        </label>
        <textarea
          className={ `w-full rounded-md border border-gray-300 p-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-indigo-500` }
          rows={ 3 }
          value={ replyBody }
          onChange={ e => setReplyBody(clampSmsToMaxParts(e.target.value, DEFAULT_MAX_SMS_PARTS)) }
          placeholder={ `Type your SMS reply...` }
        />
        <div className={ `mt-2 text-xs ${smsMetrics.parts > 1 ? `text-amber-700 font-medium` : `text-gray-600`}` }>
          { `${replyBody.length} chars · ${smsMetrics.parts}/${DEFAULT_MAX_SMS_PARTS} parts · Estimated cost: ${smsMetrics.parts} SMS part${smsMetrics.parts === 1 ? `` : `s`}` }
        </div>
        { smsMetrics.parts >= DEFAULT_MAX_SMS_PARTS ? (
          <div className={ `mt-1 text-xs text-amber-700` }>
            { `Maximum length reached (${DEFAULT_MAX_SMS_PARTS} SMS parts).` }
          </div>
        ) : null }
        <div className={ `mt-2 flex items-center justify-between` }>
          <div className={ `text-xs text-red-600` }>
            { sendReplyError ? `Failed to send SMS reply` : `` }
          </div>
          <Button
            disabled={ !canReply || isSendingReply || !hasSmsDestination }
            tooltip={ sendSmsDisabledReason }
            loading={ isSendingReply }
            color={ `paidniceGreen` }
            size={ `md` }
            onClick={ () => setConfirmOpen(true) }
          >
            { `Send SMS` }
          </Button>
        </div>
      </div>

      <Modal
        open={ confirmOpen }
        setOpen={ () => setConfirmOpen(false) }
        title={ `` }
      >
        <div className={ `space-y-4 pb-2` }>
          <Heading size={ `md` }>{ `Confirm SMS send` }</Heading>
          <Paragraph
            size={ `sm` }
            variant={ `secondary` }
          >
            { `Please review the details before sending.` }
          </Paragraph>
          <div className={ `text-sm text-gray-900` }>
            <span className={ `font-semibold` }>{ `Destination: ` }</span>
            <span>{ destinationNumber || `-` }</span>
          </div>
          <div className={ `text-sm text-gray-900` }>
            <span className={ `font-semibold` }>{ `Total cost: ` }</span>
            <span>{ `${smsMetrics.parts} SMS part${smsMetrics.parts === 1 ? `` : `s`}` }</span>
          </div>
          { (isSmsLimitReached || isSmsLimitClose) && subscriptionMetadata?.id ? (
            <div className={ `rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900` }>
              <span className={ `font-semibold` }>
                { isSmsLimitReached ? `Monthly SMS limit reached.` : `Monthly SMS limit almost reached.` }
              </span>
              { ` Usage after this send: ${projectedSmsUsage}/${monthlySmsLimit}. ` }
              <Link
                to={ `/billing/subscriptions/${subscriptionMetadata.id}` }
                className={ `underline font-medium` }
              >
                { `Modify subscription` }
              </Link>
              { `.` }
            </div>
          ) : null }
          <div>
            <div className={ `text-sm font-semibold text-gray-900 mb-1` }>{ `Message preview` }</div>
            <div className={ `rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-900 whitespace-pre-wrap wrap-break-word` }>
              { replyBody.trim() || `-` }
            </div>
          </div>

          <When condition={ !subscriptionMetadata?.smsBillingEnabled }>
            <Notification type={ `error` }
              className={ `mb-4` }>
              {
                `SMS Billing is currently disabled for the linked subscription.`
              }
              <When condition={ isBillingOwner }>
                { ` You can enable SMS billing in your ` }
                <Link to={ `/billing/subscriptions/${subscriptionMetadata.id}` }
                  className={ `underline` }>
                  { `subscription` }
                </Link>
                { `.` }
              </When>

              <When condition={ !isBillingOwner }>
                { `Ask the subscription owner to enable SMS billing` }
              </When>
            </Notification>
          </When>
          <div className={ `flex justify-end gap-2 pt-2` }>
            <Button
              color={ `gray` }
              size={ `md` }
              onClick={ () => setConfirmOpen(false) }
              disabled={ isSendingReply }
            >
              { `Cancel` }
            </Button>
            <Button
              color={ `paidniceGreen` }
              size={ `md` }
              loading={ isSendingReply }
              onClick={ onSendReply }
              disabled={ !canReply || isSendingReply || !hasSmsDestination || isSmsLimitReached || !subscriptionMetadata?.smsBillingEnabled }
            >
              { `Confirm & Send` }
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}
